import { axiosInstance } from '../../../common/axiosInstans.ts';
import axios from 'axios';
import { UserWithBarcode } from '../../../application/types/userWithBarcode.ts';

class UserServices {
    getUsers = async () => {
        return await axiosInstance
            .get('/users')
            .then((response) => response.data)
            .then((json) => json);
    };

    getInactiveUsers = async () => {
        return await axiosInstance
            .get('/users/inactive')
            .then((response) => response.data)
            .then((json) => json);
    };

    uploadCsv = async (selectedFile: File) => {
        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await axiosInstance.post(
                '/users/upload',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response;
        } catch (error) {
            console.error('Error uploading CSV file:', error);
            throw error;
        }
    };

    getUser = async (id) => {
        try {
            const res = await axiosInstance.get(`/users/${id}`);
            return res.data;
        } catch (error) {
            console.error('Error fetching user data:', error);
            throw error;
        }
    };

    getAllRoles = async () => {
        try {
            const res = await axiosInstance.get('/roles', {});
            return res.data;
        } catch (error) {
            throw error;
        }
    };

    updateUserOrganizations = async (
        userId: string,
        organizationIds: string[]
    ) => {
        try {
            let response = await axiosInstance.put(
                `/users/${userId}/organizations`,
                {
                    organizationIds,
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error updating user organizations:', error);
            throw error;
        }
    };

    getUserOrganizations = async (userId: string) => {
        try {
            const response = await axiosInstance.get(
                `/users/${userId}/organizations`
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching user organizations:', error);
            throw error;
        }
    };

    updateUserRole = async (id) => {
        try {
            const res = await axiosInstance.post(`/users/${id}/roles`, {});
            return res.data;
        } catch (error) {
            throw error;
        }
    };

    updateUserRoles = async (newRoles, id) => {
        try {
            return await axiosInstance.post(`/users/${id}/roles`, {
                userRoles: newRoles,
            });
        } catch (error) {
            console.error('Error saving roles:', error);
            throw error;
        }
    };

    resendWelcomeEmail = async (userId: string) => {
        try {
            const token =
                sessionStorage.getItem('tokens') !== null
                    ? JSON.parse(sessionStorage.getItem('tokens'))?.token
                    : null;

            const response = await axios.post(
                `${import.meta.env.VITE_NOVERSION_API_URL}/users/resend-welcome-email`,
                { userId },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error resending welcome email:', error);
            throw error;
        }
    };

    getUsersWithBarcodes = async (): Promise<UserWithBarcode[]> => {
        try {
            const response = await axiosInstance.get('/users/with-barcodes');
            return response.data;
        } catch (error) {
            console.error('Error fetching users with barcodes:', error);
            throw error;
        }
    };

    updateUser = async (
        userId: string,
        data: {
            firstName?: string;
            lastName?: string;
            barcode?: string;
            receiveInventoryReminderEmail?: boolean;
        }
    ) => {
        try {
            const response = await axiosInstance.put(`/users/${userId}`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    };

    getDeviationEmailRecipients = async (): Promise<any[]> => {
        try {
            const tenant = localStorage.getItem('orgId')
                ? JSON.parse(localStorage.getItem('orgId')!)
                : null;
            const response = await axiosInstance.get(
                '/deviationemailrecipient',
                {
                    params: { t: Date.now() },
                    headers: { 'Cache-Control': 'no-cache', tenant },
                }
            );
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            console.error('Error fetching deviation email recipients:', error);
            return [];
        }
    };

    createDeviationEmailRecipient = async (request: {
        email: string;
        name: string;
        isActive: boolean;
        description: string;
    }): Promise<any> => {
        try {
            const tenant = localStorage.getItem('orgId')
                ? JSON.parse(localStorage.getItem('orgId')!)
                : null;
            const response = await axiosInstance.post(
                '/deviationemailrecipient',
                request,
                { headers: { tenant } }
            );
            return response.data;
        } catch (error) {
            console.error('Error creating deviation email recipient:', error);
            throw error;
        }
    };

    deleteDeviationEmailRecipient = async (id: string): Promise<void> => {
        try {
            const tenant = localStorage.getItem('orgId')
                ? JSON.parse(localStorage.getItem('orgId')!)
                : null;
            await axiosInstance.delete(`/deviationemailrecipient/${id}`, {
                headers: { tenant },
            });
        } catch (error) {
            console.error('Error deleting deviation email recipient:', error);
            throw error;
        }
    };

    getLowSaldoEmailRecipients = async (): Promise<any[]> => {
        try {
            const tenant = localStorage.getItem('orgId')
                ? JSON.parse(localStorage.getItem('orgId')!)
                : null;
            const response = await axiosInstance.get(
                '/lowsaldoemailrecipient',
                {
                    params: { t: Date.now() },
                    headers: { 'Cache-Control': 'no-cache', tenant },
                }
            );
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            console.error('Error fetching low saldo email recipients:', error);
            return [];
        }
    };

    createLowSaldoEmailRecipient = async (request: {
        email: string;
        name: string;
        isActive: boolean;
        description: string;
    }): Promise<any> => {
        try {
            const tenant = localStorage.getItem('orgId')
                ? JSON.parse(localStorage.getItem('orgId')!)
                : null;
            const response = await axiosInstance.post(
                '/lowsaldoemailrecipient',
                request,
                { headers: { tenant } }
            );
            return response.data;
        } catch (error) {
            console.error('Error creating low saldo email recipient:', error);
            throw error;
        }
    };

    deleteLowSaldoEmailRecipient = async (id: string): Promise<void> => {
        try {
            const tenant = localStorage.getItem('orgId')
                ? JSON.parse(localStorage.getItem('orgId')!)
                : null;
            await axiosInstance.delete(`/lowsaldoemailrecipient/${id}`, {
                headers: { tenant },
            });
        } catch (error) {
            console.error('Error deleting low saldo email recipient:', error);
            throw error;
        }
    };
}

export default UserServices;
